import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  Divider,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Link,
  FormHelperText,
} from "@mui/material";
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  PersonAdd as RegisterIcon,
  Login as LoginIcon,
  Email as EmailIcon,
  Badge as RutIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Lock,
} from "@mui/icons-material";
import { useAuth } from "../../context/context";
import { API_URLS } from "../../config/environment";

// Función para formatear RUT chileno
const formatRUT = (rut) => {
  if (!rut) return "";

  const cleanRUT = rut.replace(/[^0-9kK]/g, "");
  if (cleanRUT.length === 0) return "";

  const rutNumber = cleanRUT.slice(0, -1);
  const verificator = cleanRUT.slice(-1).toUpperCase();

  if (rutNumber.length === 0) return cleanRUT;

  const formattedNumber = rutNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${formattedNumber}-${verificator}`;
};

// Función para validar RUT chileno
const validateRUT = (rut) => {
  if (!rut) return false;

  const cleanRUT = rut.replace(/[^0-9kK]/g, "");
  if (cleanRUT.length < 2) return false;

  const rutNumber = cleanRUT.slice(0, -1);
  const verificator = cleanRUT.slice(-1).toUpperCase();

  let sum = 0;
  let multiplier = 2;

  for (let i = rutNumber.length - 1; i >= 0; i--) {
    sum += parseInt(rutNumber[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = sum % 11;
  const calculatedVerificator =
    remainder === 0 ? "0" : remainder === 1 ? "K" : (11 - remainder).toString();

  return verificator === calculatedVerificator;
};

// Validador para email
const validateEmail = (email) => {
  if (!email || !email.trim()) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

// Validador para teléfono (+ZZYXXXXXXXX)
const validatePhone = (phone) => {
  if (!phone || !phone.trim()) return false;
  const phoneRegex = /^\+\d{1,3}\d{7,10}$/;
  return phoneRegex.test(phone.replace(/\s/g, ""));
};

// Formateador para teléfono
const formatPhone = (phone) => {
  if (!phone) return "";

  let cleaned = phone.replace(/[^\d+]/g, "");

  if (!cleaned.startsWith("+")) {
    if (cleaned.startsWith("56")) {
      cleaned = "+" + cleaned;
    } else if (cleaned.length > 0) {
      cleaned = "+56" + cleaned;
    }
  }

  return cleaned;
};

// Validador para contraseña
const validatePassword = (password) => {
  if (!password)
    return { isValid: false, message: "La contraseña es requerida" };
  if (password.length < 6)
    return { isValid: false, message: "Mínimo 6 caracteres" };
  if (!/[A-Z]/.test(password))
    return { isValid: false, message: "Debe contener al menos una mayúscula" };
  if (!/[a-z]/.test(password))
    return { isValid: false, message: "Debe contener al menos una minúscula" };
  if (!/[0-9]/.test(password))
    return { isValid: false, message: "Debe contener al menos un número" };
  return { isValid: true, message: "" };
};

// Validador para nombres (solo letras y espacios)
const validateName = (name) => {
  if (!name || !name.trim()) return false;
  const nameRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/;
  return nameRegex.test(name.trim());
};

const RegisterForm = ({ open, onClose, onSuccess, onOpenLogin }) => {
  const [formData, setFormData] = useState({
    email: "",
    taxNumber: "",
    name: "",
    lastName: "",
    secondLastName: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    // Aplicar formateo específico según el campo
    if (name === "taxNumber") {
      processedValue = formatRUT(value);
    } else if (name === "phone") {
      processedValue = formatPhone(value);
    } else if (["name", "lastName", "secondLastName"].includes(name)) {
      // Solo permitir letras y espacios para nombres
      processedValue = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, "");
    } else if (name === "email") {
      processedValue = value.toLowerCase().trim();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Limpiar errores cuando el usuario corrige
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validar email
    if (!formData.email.trim()) {
      newErrors.email = "El email es requerido";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Formato de email inválido";
    }

    // Validar RUT
    if (!formData.taxNumber.trim()) {
      newErrors.taxNumber = "El RUT es requerido";
    } else if (!validateRUT(formData.taxNumber)) {
      newErrors.taxNumber = "RUT chileno inválido";
    }

    // Validar nombre
    if (!formData.name.trim()) {
      newErrors.name = "El nombre es requerido";
    } else if (!validateName(formData.name)) {
      newErrors.name = "Solo se permiten letras y espacios";
    }

    // Validar apellido paterno
    if (!formData.lastName.trim()) {
      newErrors.lastName = "El apellido paterno es requerido";
    } else if (!validateName(formData.lastName)) {
      newErrors.lastName = "Solo se permiten letras y espacios";
    }

    // Validar apellido materno
    if (!formData.secondLastName.trim()) {
      newErrors.secondLastName = "El apellido materno es requerido";
    } else if (!validateName(formData.secondLastName)) {
      newErrors.secondLastName = "Solo se permiten letras y espacios";
    }

    // Validar teléfono
    if (!formData.phone.trim()) {
      newErrors.phone = "El teléfono es requerido";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Formato de teléfono inválido (ej: +56912345678)";
    }

    // Validar contraseña
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.message;
    }

    // Validar confirmación de contraseña
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Confirma tu contraseña";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Las contraseñas no coinciden";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      // Preparar datos según la especificación del API
      const requestBody = {
        email: formData.email.trim().toLowerCase(),
        taxNumber: formData.taxNumber.trim(),
        name: formData.name.trim(),
        lastName: formData.lastName.trim(),
        secondLastName: formData.secondLastName.trim(),
        phone: formData.phone.trim(),
        password: formData.password,
        source: "venta-usados", // Importante para auto-asociar cotizaciones
      };

      console.log("📝 Enviando registro:", {
        ...requestBody,
        password: "[HIDDEN]",
      });

      const response = await fetch(API_URLS.SIGNUP, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("📝 Respuesta del registro:", result);

      if (result.succes && !result.error) {
        // ✅ Registro exitoso
        console.log("✅ Registro exitoso");

        // Limpiar formulario
        setFormData({
          email: "",
          taxNumber: "",
          name: "",
          lastName: "",
          secondLastName: "",
          phone: "",
          password: "",
          confirmPassword: "",
        });

        // Llamar callback de éxito
        onSuccess({
          message: result.message,
          email: requestBody.email,
        });
      } else if (result.error) {
        // ❌ Error de negocio (usuario existente, etc.)
        setErrors({
          submit: result.message,
        });
      } else {
        // ❌ Respuesta inesperada
        setErrors({
          submit: "Respuesta inesperada del servidor",
        });
      }
    } catch (error) {
      console.error("❌ Error en el registro:", error);
      setErrors({
        submit: "Error de conexión. Verifica tu conexión e intenta nuevamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setErrors({});
    setLoading(false);
  };

  const handleLoginClick = () => {
    handleClose();
    if (onOpenLogin) {
      onOpenLogin();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">Crear Cuenta</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Únete para acceder a cotizaciones personalizadas y más beneficios
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {errors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.submit}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Email */}
            <TextField
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* RUT */}
            <TextField
              name="taxNumber"
              label="RUT"
              value={formData.taxNumber}
              onChange={handleChange}
              error={!!errors.taxNumber}
              helperText={errors.taxNumber}
              fullWidth
              required
              placeholder="12.345.678-9"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <RutIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Nombres */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                name="name"
                label="Nombre"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                fullWidth
                required
              />

              <TextField
                name="lastName"
                label="Apellido Paterno"
                value={formData.lastName}
                onChange={handleChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
                required
              />
            </Box>

            {/* Apellido materno y teléfono */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                name="secondLastName"
                label="Apellido Materno"
                value={formData.secondLastName}
                onChange={handleChange}
                error={!!errors.secondLastName}
                helperText={errors.secondLastName}
                fullWidth
                required
              />

              <TextField
                name="phone"
                label="Teléfono"
                value={formData.phone}
                onChange={handleChange}
                error={!!errors.phone}
                helperText={errors.phone}
                fullWidth
                required
                placeholder="+56912345678"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Contraseñas */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                name="password"
                label="Contraseña"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                name="confirmPassword"
                label="Confirmar Contraseña"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Información sobre requisitos de contraseña */}
            <FormHelperText>
              La contraseña debe tener al menos 6 caracteres, incluir
              mayúsculas, minúsculas y números
            </FormHelperText>
          </Box>

          {/* Nueva sección para volver al login */}
          {/* <Box sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 1, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              ¿Ya tienes una cuenta?
            </Typography>
            <Button
              variant="outlined"
              onClick={handleLoginClick}
              startIcon={<Login />}

              sx={{ textTransform: "none" }}
              disabled={loading}
            >
              Iniciar Sesión
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Accede con tu cuenta existente
            </Typography>
          </Box> */}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleLoginClick} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? "Registrando..." : "Crear Cuenta"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RegisterForm;
